import { Button } from "components/DesignSystem/Button/Button";
import { DateInput } from "components/DesignSystem/DateInput/DateInput";
import Modal from "components/DesignSystem/Modal/Modal";
import { TextInput } from "components/DesignSystem/TextInput/TextInput";
import { PatternInput } from "components/PatternInput/PatternInput";
import { PriceInput } from "components/PriceInput/PriceInput";
import { DISPLAY_DATE } from "constants/date";
import dayjs from "dayjs";
import {
  Field,
  FieldProps,
  FormikHelpers,
  FormikErrors,
  useFormikContext,
} from "formik";
import { FormikForm } from "components/FormikForm/FormikForm";
import { newAccount } from "formValidations/coaCreation";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useToast } from "hooks/useToast";
import { useState, useMemo, useEffect } from "react";
import {
  useAddTransactionCategoryMutation,
  useGetAutoGenerateNextAvailableIdentiferQuery,
  useGetDefaultCategoryTagQuery,
  useGetStartingDateQuery,
  useGetTransactionCategoryQuery,
} from "store/apis/chartOfAccounts";
import { associatedReport } from "utils/associatedReport";
import { CategoryTag } from "./EditCategory";
import {
  BooksLockDateResponse,
  useGetBookkeepingSettingsQuery,
} from "store/apis/booksLockDate";
import ConditionalToolTip from "components/design/conditionalToolTip";
import { LockDateToolTipContent } from "components/LockedDatePicker/LockedDatePicker";
import { CategorySelector } from "components/CategorySelector/CategorySelector";
import { TxnCategories } from "types/Models/books";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { BackendError } from "types/utils/error";
import { useTypedFormikContext } from "hooks/useTypedFormikContext";
import { InfoItem } from "components/DesignSystem/InfoItem/InfoItem";

type ParentSelectorProps = {
  close: () => void;
  isOpen: boolean;
  open: () => void;
  categoryName?: string;
  initialAccountType?: string;
  onSuccess?: ({
    uuid,
    name,
    category_type,
  }: Pick<TxnCategories, "uuid" | "name" | "category_type">) => void;
};

type AddAccountFormValues = {
  parent_uuid: string;
  account_id_prefix: 1 | 2 | 3 | 4 | 5 | undefined;
  account_name: string;
  account_type: string;
  account_id: string;
  description: string;
  start_date: string;
  opening_balance: string;
  isBalanceSheetAssociated: boolean;
  category_tag: string;
};

const AddAccountFormBody = ({
  selectedAccountType,
  setSelectedAccountType,
  parent_uuid,
  identifier_prefix,
  availableIdentifier,
  start_date,
  default_tag,
  isBalanceSheetAssociated,
  bookkeepingSettings,
  isAccountTypePrefilled,
  accountTypeName,
}: {
  selectedAccountType: string;
  setSelectedAccountType: (value: string) => void;
  parent_uuid?: string;
  identifier_prefix?: 1 | 2 | 3 | 4 | 5;
  availableIdentifier?: number;
  start_date?: string;
  default_tag?: string;
  isBalanceSheetAssociated: boolean;
  bookkeepingSettings?: BooksLockDateResponse;
  isAccountTypePrefilled?: boolean;
  accountTypeName?: string;
}) => {
  const { values, setFieldValue } =
    useTypedFormikContext<AddAccountFormValues>();

  useEffect(() => {
    if (!selectedAccountType) return;

    if (parent_uuid) {
      setFieldValue("parent_uuid", parent_uuid);
    }
    if (identifier_prefix) {
      setFieldValue("account_id_prefix", identifier_prefix);
    }
    if (availableIdentifier) {
      setFieldValue("account_id", availableIdentifier.toString());
    }
    if (start_date) {
      setFieldValue("start_date", start_date);
    }
    if (default_tag !== undefined) {
      setFieldValue("category_tag", default_tag || "");
    }
    setFieldValue("isBalanceSheetAssociated", isBalanceSheetAssociated);
  }, [
    selectedAccountType,
    parent_uuid,
    identifier_prefix,
    availableIdentifier,
    start_date,
    default_tag,
    isBalanceSheetAssociated,
  ]);

  const handleCategoryChange = ({
    value,
    setFieldValue,
  }: {
    value: {
      label: string;
      value: string;
      categoryType: string;
      data: string;
      name: string;
    } | null;
    setFieldValue: <T extends keyof AddAccountFormValues>(
      field: T,
      value: AddAccountFormValues[T],
      shouldValidate?: boolean
    ) => Promise<void | FormikErrors<AddAccountFormValues>>;
  }) => {
    if (value) {
      setFieldValue("account_type", value.value);
      setSelectedAccountType(value.value);
    } else {
      setFieldValue("account_type", "");
      setSelectedAccountType("");
      setFieldValue("account_id", "");
      setFieldValue("account_id_prefix", undefined);
      setFieldValue("start_date", "");
      setFieldValue("opening_balance", "0");
      setFieldValue("category_tag", "");
      setFieldValue("parent_uuid", "");
    }
  };

  return (
    <div className="t-flex t-flex-col t-gap-5">
      <TextInput
        name="account_name"
        label="Account name"
        required
        placeholder="Enter Account name"
      />

      {isAccountTypePrefilled ? (
        <InfoItem label="Account type">
          <div className="t-text-body-sm t-text-text-100">
            {accountTypeName}
          </div>
        </InfoItem>
      ) : (
        <CategorySelector
          withForm
          name="account_type"
          label="Account type"
          placeholder="Select account type for which you are adding"
          category={values.account_type}
          required
          onChange={(value) => handleCategoryChange({ value, setFieldValue })}
          menuPortalStyle={{
            width: 496,
          }}
          hiddenCategoryTypes={[
            "BANK_ACCOUNT",
            "BANK_TRANSFER",
            "PAY_DOWN_CREDIT",
          ]}
          hideAction
          enableTopLevel
          hideError={false}
        />
      )}

      {values.account_id_prefix ? (
        <PatternInput
          fieldProps={{
            name: "account_id",
            value: values.account_id,
          }}
          patternProps={{
            format: `${values.account_id_prefix}####`,
            mask: "_",
            allowEmptyFormatting: true,
          }}
          label="Account ID"
        />
      ) : (
        <TextInput name="account_id" label="Account ID" disabled />
      )}

      <TextInput
        name="description"
        label="Description"
        placeholder="Explanation of account"
      />
      {isBalanceSheetAssociated && (
        <div className="t-flex t-gap-4">
          <div className="t-w-1/2 sm:t-min-w-0">
            <Field name="start_date">
              {({ field }: FieldProps) => {
                return (
                  <DateInput
                    disabled
                    {...field}
                    label="Starting date"
                    placeholder="Select date"
                    block
                  />
                );
              }}
            </Field>
          </div>
          <div className="t-w-1/2 sm:t-min-w-0">
            <ConditionalToolTip
              propClass="t-w-64 !t-text-left react-datepicker-ignore-onclickoutside"
              condition={
                Boolean(bookkeepingSettings?.accounting_lock_date) && (
                  <LockDateToolTipContent text="The opening balance cannot be edited since the financials are locked." />
                )
              }
            >
              <span>
                <PriceInput
                  disabled={
                    !Boolean(values.start_date) ||
                    Boolean(bookkeepingSettings?.accounting_lock_date)
                  }
                  name="opening_balance"
                  label="Account balance"
                  placeholder={`As on ${
                    values.start_date
                      ? dayjs(values.start_date)
                          .subtract(1, "days")
                          .format(DISPLAY_DATE)
                      : "_"
                  }`}
                  required
                />
              </span>
            </ConditionalToolTip>
          </div>
        </div>
      )}

      <CategoryTag
        parentId={parent_uuid!}
        required={isBalanceSheetAssociated}
      />
    </div>
  );
};

export const AddNewAccount = ({
  isOpen,
  close,
  onSuccess,
  categoryName,
  initialAccountType = "",
}: ParentSelectorProps) => {
  const { alertToast, successToast } = useToast();
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const [selectedAccountType, setSelectedAccountType] =
    useState<string>(initialAccountType);
  const [createTransactionCategory, { isLoading }] =
    useAddTransactionCategoryMutation();

  const { data: selectedParentCategoryDetails } =
    useGetTransactionCategoryQuery(
      { categoryId: selectedAccountType, entityId, groupId },
      { skip: !selectedAccountType || !entityId || !groupId }
    );

  const { data: { identifier: availableIdentifier } = { identifier: 0 } } =
    useGetAutoGenerateNextAvailableIdentiferQuery(
      { groupId, entityId, parentId: selectedAccountType },
      {
        skip: !selectedAccountType || !entityId || !groupId,
        refetchOnMountOrArgChange: true,
      }
    );

  const { data: { default_tag } = {} } = useGetDefaultCategoryTagQuery(
    {
      entityId,
      parentId: selectedAccountType,
    },
    { skip: !entityId || !selectedAccountType }
  );

  const { data: startDateData } = useGetStartingDateQuery(
    {
      groupId,
      entityId,
    },
    { skip: !groupId || !entityId }
  );

  const { start_date } = startDateData || {};

  const createAccountCategory = async (
    values: AddAccountFormValues,
    formikHelpers?: FormikHelpers<AddAccountFormValues>
  ) => {
    const payload = {
      parent_uuid: values.parent_uuid,
      name: values.account_name,
      identifier: Number(values.account_id),
      description: values.description,
      start_date: values.start_date,
      opening_balance: Number(values.opening_balance),
      category_tag: values.category_tag,
    };
    try {
      const { uuid, name, category_type } = await createTransactionCategory({
        groupId,
        entityId,
        payload,
      }).unwrap();
      onSuccess?.({ uuid, name, category_type });
      close();
      formikHelpers?.resetForm();
      setSelectedAccountType("");
      successToast({ title: "Category added successfully" });
    } catch (error) {
      alertToast({ message: (error as BackendError).data?.error?.message });
    }
  };

  const { identifier_prefix, uuid: parent_uuid } =
    selectedParentCategoryDetails || {};

  const { isBalanceSheetAssociated } = associatedReport(identifier_prefix!);

  const { data: bookkeepingSettings } = useGetBookkeepingSettingsQuery(
    {
      entityId,
    },
    { skip: !entityId }
  );

  return (
    <Modal.Root open={isOpen} onOpenChange={close} modal={false}>
      <Modal.Content asChild useCustomOverlay>
        <FormikForm
          initialValues={{
            parent_uuid: parent_uuid || "",
            account_id_prefix: identifier_prefix,
            account_name: categoryName || "",
            account_type: selectedAccountType,
            account_id: availableIdentifier
              ? availableIdentifier.toString()
              : "",
            start_date: start_date || "",
            opening_balance: "0",
            description: "",
            isBalanceSheetAssociated,
            category_tag: default_tag || "",
          }}
          onSubmit={createAccountCategory}
          validationSchema={newAccount}
        >
          {({ isSubmitting, submitForm }) => (
            <>
              <Modal.Header>
                <Modal.Title>Add Account</Modal.Title>
                <Modal.Close type="button" />
              </Modal.Header>

              <Modal.Body>
                <AddAccountFormBody
                  selectedAccountType={selectedAccountType}
                  setSelectedAccountType={setSelectedAccountType}
                  parent_uuid={parent_uuid}
                  identifier_prefix={identifier_prefix}
                  availableIdentifier={availableIdentifier}
                  start_date={start_date}
                  default_tag={default_tag}
                  isBalanceSheetAssociated={isBalanceSheetAssociated}
                  bookkeepingSettings={bookkeepingSettings}
                  isAccountTypePrefilled={Boolean(initialAccountType)}
                  accountTypeName={selectedParentCategoryDetails?.name}
                />
              </Modal.Body>
              <Modal.Footer className="t-flex t-justify-end t-gap-3">
                <Button onClick={close}>Back</Button>
                <Button
                  customType="primary"
                  isLoading={isLoading}
                  disabled={isLoading || isSubmitting}
                  onClick={submitForm}
                >
                  Create
                </Button>
              </Modal.Footer>
            </>
          )}
        </FormikForm>
      </Modal.Content>
    </Modal.Root>
  );
};
